import React, { useState, useCallback } from 'react';
import StandardModal from 'components/consumer/StandardModal';
import avo from 'analytics/avo';
import PropTypes from 'prop-types';
import CtaLink from 'components/essentials/Cta/CtaLink';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import ArrowDownSimple from 'components/icons/iconArrowDownSimple';
import { colorBrand } from 'styles/layout/colors';

const formatDistance = distanceInMiles => {
  if (!distanceInMiles && distanceInMiles !== 0) return '';
  return distanceInMiles < 1
    ? `${Math.round(distanceInMiles * 10) / 10} mi`
    : `${Math.round(distanceInMiles)} mi`;
};

const formatStoreHours = hoursString => {
  if (!hoursString) return [];
  return hoursString
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);
};

const handleBrowseProducts = store => {
  const { storeData } = store;
  const showroomUrl = storeData?.rs_store_landing_page || '';

  avo.ctaClicked({
    text: storeData?.name,
    targetUrl: showroomUrl,
    cta: 'Retail Store',
    location: 'Category Page',
  });
  window.open(showroomUrl, '_blank');
};

const ProductCardMultipleStoresModal = ({ stores = [], isOpen, onClose }) => {
  const [expandedStoreIndex, setExpandedStoreIndex] = useState(0);

  const toggleStore = useCallback(
    index => {
      if (expandedStoreIndex !== index) {
        setExpandedStoreIndex(index);
      }
    },
    [expandedStoreIndex]
  );

  if (!stores || stores.length === 0) {
    return null;
  }

  return (
    <StandardModal
      className="ProductCardMultipleStoresModal"
      isOpen={isOpen}
      theme="light"
      onClose={onClose}
    >
      <div>
        {stores.map((store, index) => {
          const { storeData, distance } = store;
          const isExpanded = expandedStoreIndex === index;
          const isFirst = index === 0;
          const distanceText = formatDistance(distance);
          const storeHours = formatStoreHours(storeData?.open_hours);
          const storeLocationUrl = storeData?.google_map || '';
          const storeLocation = `${storeData?.street}, ${storeData?.city}, ${storeData?.state} ${storeData?.zip}`;
          const storeCalendly = storeData?.calendaly || '';
          const storeShowroomUrl = storeData?.rs_store_landing_page || '';

          return (
            <div
              key={storeData?.id || index}
              className={isFirst ? '' : 'border-t border-gray-light6 pt-4 mt-8'}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between cursor-pointer bg-transparent border-0 p-0 text-left mb-2"
                onClick={() => toggleStore(index)}
                aria-expanded={isExpanded}
                aria-label={`Toggle ${storeData?.name} details`}
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-brand m-0">
                    {storeData?.name} Showroom
                  </h3>
                  {distanceText && (
                    <span className="text-sm text-gray-light2">
                      ({distanceText})
                    </span>
                  )}
                </div>
                <ArrowDownSimple
                  color={colorBrand}
                  className={`transition-transform duration-300 ease-in-out ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`transition-opacity duration-300 ease-in-out ${
                  isExpanded ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'
                }`}
              >
                <div className="flex flex-col md:flex-row items-start mt-8">
                  <div className="flex flex-col items-start">
                    <h4 className="text-lg font-bold mb-4">Store Hours</h4>
                    {storeHours.length > 0 ? (
                      storeHours.map(hourData => (
                        <p
                          className="text-xs font-bold text-gray-light2 whitespace-nowrap leading-normal tracking-wide"
                          key={hourData}
                        >
                          {hourData}
                        </p>
                      ))
                    ) : (
                      <p className="text-xs font-bold text-gray-light2 tracking-wide">
                        Hours not available
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-start md:pl-8 pt-8 md:pt-0">
                    <h4 className="text-lg font-bold mb-4">Location</h4>
                    <a
                      className="text-sm text-brand font-bold no-underline"
                      href={storeLocationUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {storeLocation}
                    </a>
                    {storeCalendly && (
                      <CtaLink
                        size="base"
                        color="brand"
                        styling="solid-button"
                        to={storeCalendly}
                        className="my-6"
                      >
                        Book a Virtual Appointment
                      </CtaLink>
                    )}
                    {storeShowroomUrl && (
                      <button
                        type="button"
                        className="text-sm text-brand font-bold no-underline"
                        onClick={() => handleBrowseProducts(store)}
                        aria-label="Browse products in Showroom"
                      >
                        Browse products on display here
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </StandardModal>
  );
};

ProductCardMultipleStoresModal.propTypes = {
  isOpen: PropTypes.bool,
  stores: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func,
};

export default withBrowserOnly(ProductCardMultipleStoresModal);
